import type Luminary from '@lumen5/luminary';
import { getGlobalFontManager } from '@lumen5/luminary';

import type { MusicDescription, TemplateDescription, VideoDescription } from '../types';

import { MusicRegistry } from 'js/src/agent/registries/MusicRegistry';
import { getColorName } from 'js/src/agent/utils';

const renderColorAsString = (color: number): string => {
    return `${color}(${getColorName(color)})`;
};

export function getTemplateFontFamilies(luminary: Luminary): string[] {
    // Get the global font manager to access all available fonts
    const fontManager = getGlobalFontManager();
    const templateFonts = luminary.template?.fonts || [];

    // Filter and return only the fonts that are actually used in the template
    const fontFamilies = templateFonts
        .map((url) => {
            let font = fontManager.getFontBySource(url);
            if (!font) {
                // Luminary only loads fonts when building the asset
                font = fontManager.addFont(url);
            }
            return font ? font.familyName : null;
        })
        .filter((font) => font !== null);

    return fontFamilies;
}

export function getTemplateFontFamily(luminary: Luminary, fontURL: string): string | null {
    // Get the global font manager to access all available fonts
    const fontManager = getGlobalFontManager();
    const templateFonts = luminary.template?.fonts || [];

    // Find the first font that matches the requested URL
    for (const url of templateFonts) {
        const font = fontManager.getFontBySource(url);
        if (font && url === fontURL) {
            return font.familyName;
        }
    }

    return null; // No matching font found
}

export function getTemplateFontURL(luminary: Luminary, fontFamily: string): string | null {
    // Get the global font manager to access all available fonts
    const fontManager = getGlobalFontManager();
    const templateFonts = luminary.template?.fonts || [];

    // Find the first font that matches the requested family
    for (const url of templateFonts) {
        const font = fontManager.getFontBySource(url);
        if (font && font.familyName === fontFamily) {
            return url;
        }
    }

    return null; // No matching font found
}

export const getVideoDescription = (luminary: Luminary): VideoDescription => {
    return {
        resolution: `${luminary.referenceResolution.width}x${luminary.referenceResolution.height}`,
        aspectRatio: luminary.aspectRatio,
        sceneCount: luminary.scenes.length,
        duration: `${luminary.duration}ms`,
    };
};

/**
 * Get template metadata including ID, fonts, and color palette.
 * @param luminary The Luminary instance containing template data
 * @returns Object with template ID, font families, and color descriptions
 */
export const getTemplateDescription = (luminary: Luminary): TemplateDescription => {
    return {
        template: luminary.template.id,
        templateFonts: getTemplateFontFamilies(luminary).join(', '),
        templateColors: luminary.template.colors
            .map((item) => renderColorAsString(item.color))
            .join(', '),
    };
};

export const getVideoMusicDescription = (luminary: Luminary): MusicDescription => {
    if (luminary.music && luminary.music.uuid) {
        const musicRegistry = MusicRegistry.getInstance();
        const musicId = musicRegistry.getItemIdByUuid(luminary.music.uuid);
        if (musicId) {
            const musicRepresentation = musicRegistry.getRepresentation(musicId);
            if (musicRepresentation) {
                return {
                    uuid: luminary.music.uuid,
                    volume: luminary.musicAsset.volume,
                    title: musicRepresentation.title || 'Untitled',
                };
            }
        }
    }
    return {
        uuid: '<unknown>',
        volume: 0,
        title: 'No music set',
    };
};
